'use client';

import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ContactForm } from '@/types/contact-forms';

const SalesCallbackWidget = lazy(() => import('./SalesCallbackWidget'));

export type SalesCallbackVariant = 'sales' | 'corporate' | 'calculator';

interface SalesCallbackContextValue {
  variant: SalesCallbackVariant;
  isTabVisible: boolean;
  isFormOpen: boolean;
  showTab: () => void;
  openForm: () => void;
  closeForm: () => void;
}

const SalesCallbackContext = createContext<SalesCallbackContextValue | null>(null);

export function useSalesCallback() {
  const ctx = useContext(SalesCallbackContext);
  if (!ctx) {
    throw new Error('useSalesCallback must be used within SalesCallbackProvider');
  }
  return ctx;
}

export function useSalesCallbackOptional() {
  return useContext(SalesCallbackContext);
}

interface SalesCallbackProviderProps {
  children: ReactNode;
  variant?: SalesCallbackVariant;
  formConfig?: ContactForm | null;
}

export default function SalesCallbackProvider({
  children,
  variant = 'sales',
  formConfig,
}: SalesCallbackProviderProps) {
  const [isTabVisible, setIsTabVisible] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const showTab = useCallback(() => {
    setIsTabVisible(true);
  }, []);

  const openForm = useCallback(() => {
    setIsTabVisible(true);
    setIsFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
  }, []);

  const value = useMemo<SalesCallbackContextValue>(
    () => ({
      variant,
      isTabVisible,
      isFormOpen,
      showTab,
      openForm,
      closeForm,
    }),
    [closeForm, isFormOpen, isTabVisible, openForm, showTab, variant],
  );

  return (
    <SalesCallbackContext.Provider value={value}>
      {children}
      {isTabVisible ? (
        <Suspense fallback={null}>
          <SalesCallbackWidget formConfig={formConfig} />
        </Suspense>
      ) : null}
    </SalesCallbackContext.Provider>
  );
}
